import React, { useRef, useEffect, forwardRef } from 'react'
import classNames from 'classnames'
import styles from './Input.module.css'
import { default as generalStyle } from '../Theme/GeneralTheme.module.css'

const Input_ = ({
  iconInsideLeft = false,
  iconInsideRight = false,
  small,
  large,
  ...props
}, ref) => {

  const { className, ...rest } = props;
  const childrenClassNames = classNames([
    generalStyle.general_style,
    styles.input,
    small && styles.sm,
    large && styles.lg,
    iconInsideLeft && styles.iconInsideLeft,
    iconInsideRight && styles.iconInsideRight,
  ], className)

  return (
    <input
      className={childrenClassNames}
      ref={ref}
      {...rest}
    />
  )
}

const TextArea_ = ({
  charCount,
  resizable = true,
  ...props
}, ref) => {

  const { className, ...rest } = props;
  const childrenClassNames = classNames([
    generalStyle.general_style,
    styles.input,
    !resizable && styles.remove_resize,
  ], className)

  return (
    <React.Fragment>
      <textarea
        className={childrenClassNames}
        ref={ref}
        {...rest}
      />
      <div className={classNames([styles.textarea_counter])}>
        {props.maxLength && (
          <span>
            {charCount || props.value?.length || 0}/{props.maxLength}
          </span>
        )}
      </div>
    </React.Fragment>
  )
}

function InputFile({
  files,
  onChange,
  dragAndDrop = false,
  ...props
}) {
  const inputFileRef = useRef(null)
  const dragAndDropRef = useRef(null)

  useEffect(() => {
    dragAndDropRef.current.addEventListener('dragover', handleDrag)
    dragAndDropRef.current.addEventListener('drop', handleDrop)

    return () => {
      dragAndDropRef?.current?.removeEventListener('dragover', handleDrag)
      dragAndDropRef?.current?.removeEventListener('drop', handleDrop)
    }
  }, [])

  function handleDrag(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    if (dragAndDrop) {
      onChange(Array.from(e.dataTransfer.files))
    }
  }

  function handleOpenFileInput() {
    inputFileRef.current.click()
  }

  function onSelectFiles(e) {
    onChange(Array.from(e.target.files))
  }

  const { className, ...rest } = props;
  const childrenClassNames = classNames([
    generalStyle.general_style,
    styles.input,
    props.disabled ? styles.inputfile_disabled : styles.inputfile,
    files && !props.disabled && styles.inputfile_selected,
  ], className)

  return (
    <div
      tabIndex={-1}
      ref={dragAndDropRef}
      className={childrenClassNames}
      onClick={handleOpenFileInput}
    >
      {!files ? (
        <span className={classNames(styles.placeholder_external)}>
          {props.placeholder}
        </span>
      ) : (
        <span>{files?.map((file) => file?.name).join(', ')}</span>
      )}

      <input
        type="file"
        className={classNames([styles.input, styles.customInputFile])}
        ref={inputFileRef}
        onChange={onSelectFiles}
        {...rest}
      />
      <div className={classNames([dragAndDrop && styles.drag_and_drop])} />
    </div>
  )
}

const InputFileNative_ = ({
  ...props
}, ref) => {

  const { className, ...rest } = props;
  const childrenClassNames = classNames(styles.input, className)

  return (
    <input
      type="file"
      className={childrenClassNames}
      ref={ref}
      {...rest}
    />
  )
}

const InputSearch_ = ({
  searchLine = false,
  ...props
}, ref) => {

  const { className, ...rest } = props;
  const childrenClassNames = classNames([
    generalStyle.general_style,
    styles.input,
    styles.inputSearch,
    styles.iconInsideLeft,
    styles.iconInsideRight,
    searchLine && styles.input_search_line,
  ], className)

  return (
    <input
      className={childrenClassNames}
      type="search"
      ref={ref}
      {...rest}
    />
  )
}

const Input = forwardRef(Input_);
const TextArea = forwardRef(TextArea_);
const InputSearch = forwardRef(InputSearch_);
const InputFileNative = forwardRef(InputFileNative_);

export { Input, TextArea, InputFile, InputFileNative, InputSearch }
